import { Injectable, OnDestroy } from '@angular/core';
import { CoinbaseWalletProvider, createCoinbaseWalletSDK, ProviderInterface } from '@coinbase/wallet-sdk';
import { ethers, Interface } from 'ethers';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { NetworksInterface, Web3TransactionConfig } from 'src/app/shared/interfaces/deposit.interface';
import { environment } from 'src/environments/environment';
import { AccountApi } from '../../apis/account.api';
import { Web3WalletSignatureInfo, Web3WalletType } from '../../interfaces/auth.interface';
import { Web3WalletAdapter } from '../../interfaces/web3-wallet.interface';
import { LocalStorageService } from '../localstorage.service';
import { ToastService } from '../toast.service';

@Injectable({
  providedIn: 'root',
})
export class CoinbaseWalletService implements Web3WalletAdapter, OnDestroy {
  // 根據環境設定支援的網路 [mainnet, bsc, polygon] : [sepolia, bscTestnet, polygonMumbai]
  private supportedNetworks = environment.isOnline ? [1, 56, 137] : [11155111, 97, 80001];

  constructor(
    private appService: AppService,
    private toastService: ToastService,
    private accountApi: AccountApi,
    private localStorageService: LocalStorageService,
    private deviceService: DeviceDetectorService,
  ) {
    this.sdk = createCoinbaseWalletSDK({
      appName: 'PUBS.com',
      appLogoUrl: `${window.location.origin}/assets/resources/web3-wallets/dapp-logo.png`,
      preference: { options: 'eoaOnly' },
    });

    this.provider = this.sdk.getProvider() as CoinbaseWalletProvider;

    // 在服務初始化時就設定監聽事件
    this.setupEventListeners();

    // 檢查已存在的連接
    this.checkExistingConnection();
  }

  private sdk: {
    getProvider: () => ProviderInterface;
  } | null = null;
  private provider: CoinbaseWalletProvider | null = null;

  private readonly _isConnected = new BehaviorSubject<boolean>(false);
  private readonly _accounts = new BehaviorSubject<string[]>([]);
  private readonly _currentChainId = new BehaviorSubject<number | string | null>(null);

  public readonly isConnected$: Observable<boolean> = this._isConnected.asObservable();
  public readonly accounts$: Observable<string[]> = this._accounts.asObservable();
  public readonly currentChainId$: Observable<number | string | null> = this._currentChainId.asObservable();

  // 判斷是否為行動裝置
  private isMobileDevice =
    this.deviceService.isMobile() ||
    this.deviceService.isTablet() ||
    navigator.userAgent.match(/Android/i) !== null ||
    navigator.userAgent.match(/iPhone|iPad/i) !== null;

  /**
   * 設定錢包事件監聽
   */
  private setupEventListeners() {
    if (!this.provider) return;

    // 監聽帳戶變更事件 (EIP-1193)
    this.provider.on('accountsChanged', this.handleAccountsChanged);

    // 監聽網路鏈 (Chain) 變更事件 (EIP-1193)
    this.provider.on('chainChanged', this.handleChainChanged);
  }

  /**
   * 移除錢包監聽事件
   */
  private removeEventListeners() {
    if (!this.provider) return;

    // 移除監聽事件
    this.provider.removeListener('accountsChanged', this.handleAccountsChanged);
    this.provider.removeListener('chainChanged', this.handleChainChanged);
  }

  /**
   * 處理帳戶變更事件
   *
   * @param accounts - 新的帳戶
   */
  private handleAccountsChanged = async (accounts: string[]) => {
    const currWalletType = this.localStorageService.getConnectedWeb3WalletType();

    // 只有在 coinbase wallet 連線狀態下才處理帳號切換
    if (this._isConnected.getValue() && currWalletType === Web3WalletType.CoinbaseWallet) {
      if (accounts.length === 0) {
        // 如果陣列為空，代表使用者已在錢包中斷開所有帳戶的連接
        await this.disconnectWallet();
      } else {
        const curAccounts = this._accounts.getValue();

        // 判斷已經有連結帳戶，且當前帳戶與新帳戶不同
        if (curAccounts[0] && curAccounts[0] !== accounts[0]) {
          // 如果帳戶變更，直接登出
          await this.disconnectWallet();
          this.appService.logoutSubject$.next(true);
        }
      }
    }
  };

  /**
   * 處理網路鏈變更事件
   *
   * @param chainIdHex - 新的鏈 ID (格式為 0x 開頭的十六進制字符串)
   */
  private handleChainChanged = (chainIdHex: string) => {
    const currWalletType = this.localStorageService.getConnectedWeb3WalletType();

    // 只有在 coinbase wallet 連線狀態下才處理帳號切換
    if (this._isConnected.getValue() && currWalletType === Web3WalletType.CoinbaseWallet) {
      const chainIdDec = parseInt(chainIdHex, 16);
      this._currentChainId.next(chainIdDec);
    }
  };

  /**
   * 移除監聽事件
   */
  ngOnDestroy(): void {
    this.removeEventListeners();
  }

  /**
   * 更新連線狀態
   *
   * @param isConnected - 是否已連線
   */
  updateConnectedStatus(isConnected: boolean) {
    this._isConnected.next(isConnected);
  }

  // 檢查已存在的連接
  async checkExistingConnection(): Promise<boolean> {
    if (!this.provider) return false;

    try {
      const existingAccounts = await this.provider.request<string[]>({ method: 'eth_accounts' });
      const isLogin = Boolean(this.localStorageService.loginToken);
      if (existingAccounts && existingAccounts.length > 0 && isLogin) {
        this._accounts.next(existingAccounts);

        // Reload後，判斷已連線的web3錢包是否為綁定於DB的web3錢包
        const web3WalletRes = await firstValueFrom(this.accountApi.getWeb3Wallet());
        const connectedWallets = web3WalletRes.data || [];
        const isExistingWallet = connectedWallets.some(
          wallet => wallet.address.toLowerCase() === existingAccounts[0].toLowerCase(),
        );

        this.updateConnectedStatus(isExistingWallet);
        await this.fetchCurrentChainId(); // 獲取當前的 Chain ID

        return isExistingWallet;
      }
    } catch (err) {
      console.error('Error checking existing connection in service:', err);
    }

    return false;
  }

  /**
   * 請求連接到 Coinbase Wallet
   */
  async connectWallet() {
    if (!this.provider) {
      const errMsg = 'SDK Provider is not initialized.';
      console.error(errMsg);
      return;
    }

    try {
      // 請求帳戶
      const requestedAccounts = await this.provider.request<string[]>({ method: 'eth_requestAccounts' }).catch(err => {
        console.error('Error requesting accounts:', err);
      });

      if (requestedAccounts && requestedAccounts.length > 0) {
        this._accounts.next(requestedAccounts);
        this.updateConnectedStatus(false);
        // 連接成功後，獲取一次當前的 Chain ID
        await this.fetchCurrentChainId();
      } else {
        const warnMsg = 'Connected successfully, but have not accessed accounts yet.';
        console.warn(warnMsg);
        this.updateConnectedStatus(false);
      }
    } catch (err) {
      // 使用手機時，直接開啟 Coinbase Wallet 應用程式
      this.openInCoinbaseWalletBrowser();

      this.updateConnectedStatus(false);
      this._accounts.next([]);
    }
  }

  /**
   * 斷開與錢包的連接並重置狀態
   */
  async disconnectWallet() {
    // 移除事件監聽
    this.removeEventListeners();

    if (this.provider) {
      if (typeof (this.provider as any).disconnect === 'function') {
        await (this.provider as any).disconnect();
      } else if (typeof (this.provider as any).close === 'function') {
        await (this.provider as any).close();
      }

      // this.provider = null;
      // this._provider.next(this.provider);
    }

    // 重置所有狀態
    this._accounts.next([]);
    this.updateConnectedStatus(false);
    this._currentChainId.next(null);
  }

  /**
   * 簽署消息
   *
   * @param message
   */
  async signMessage(message: string): Promise<Web3WalletSignatureInfo | null> {
    if (!this.provider || this._accounts.getValue().length === 0) {
      return null;
    }

    /**
     * coinbase wallet 需要將 message 轉換為 hex 格式
     * 這裡使用 Buffer 將 message 轉換為 hex 格式
     * 這是因為 Coinbase Wallet 的 personal_sign 方法需要 hex 格式的消息
     * 這裡的 message 是一個字符串，轉換為 hex 格式
     * 例如：'Hello, World!' 轉換為 '0x48656c6c6f2c20576f726c6421'
     * 這裡的 Buffer.from(message, 'utf8').toString('hex') 將字符串轉換為 hex 格式
     * 這裡的 'utf8' 是字符串的編碼格式
     * 這裡的 'hex' 是轉換後的格式
     * 這裡的 '0x' 是 hex 格式的前綴
     */
    const hexMessage = '0x' + Buffer.from(message, 'utf8').toString('hex');
    const signerAddress = this._accounts.getValue()[0];

    const signature = await this.provider.request<string>({
      method: 'personal_sign',
      params: [hexMessage, signerAddress],
    });

    // 連接成功後，獲取當前的 Chain ID
    const chainId = await this.fetchCurrentChainId();

    if (chainId) {
      return {
        walletType: Web3WalletType.CoinbaseWallet,
        network: chainId.toString(),
        address: signerAddress,
        signature,
      };
    }

    return null;
  }

  /**
   * 獲取當前連接的網路 Chain ID
   */
  async fetchCurrentChainId() {
    if (!this.provider) {
      this._currentChainId.next(null); // 清除舊值
      return null;
    }

    try {
      // 使用 EIP-1193 標準方法 eth_chainId
      const chainIdHex = await this.provider.request<string>({ method: 'eth_chainId' });
      const chainIdDec = parseInt(chainIdHex, 16); // 將 16 進制轉為 10 進制
      this._currentChainId.next(chainIdDec);

      return chainIdDec;
    } catch (err) {
      console.error('Fetch Chain ID failed:', err);
      return null;
    }
  }

  /**
   * 獲取第一個連接帳戶的餘額
   */
  async fetchBalance() {
    const currentAccounts = this._accounts.getValue();
    if (!this.provider || !this._isConnected.getValue() || currentAccounts.length === 0) {
      return null;
    }

    // Coinbase Wallet 在手機端無法取得餘額，直接返回 0
    if (this.isMobileDevice) {
      return '0';
    }

    try {
      const address = currentAccounts[0]; // 獲取第一個帳戶地址

      // 用 SDK provider 初始化 ethers provider
      const ethersProvider = new ethers.BrowserProvider(this.provider);
      const balanceWei = await ethersProvider.getBalance(address);
      const formattedBalance = ethers.formatEther(balanceWei);

      return formattedBalance;
    } catch (err) {
      console.error('Fetch balance error:', err);
      return null;
    }
  }

  /**
   * 獲取合約帳戶的餘額
   *
   * @param network
   */
  async fetchContractBalance(network: NetworksInterface) {
    const currentAccounts = this._accounts.getValue();
    if (!this.provider || !this._isConnected.getValue() || currentAccounts.length === 0) {
      return null;
    }

    // Coinbase Wallet 在手機端無法取得餘額，直接返回 0
    if (this.isMobileDevice) {
      return '0';
    }

    try {
      const address = currentAccounts[0]; // 獲取第一個帳戶地址

      // 用 SDK provider 初始化 ethers provider
      const ethersProvider = new ethers.BrowserProvider(this.provider);

      const { contractAddress, contractABI, decimals } = network;
      const contract = new ethers.Contract(contractAddress, contractABI, ethersProvider);
      const curBalance = await contract.balanceOf(address).then(data => {
        const balance = data / BigInt(10 ** decimals);
        const remainder = data % BigInt(10 ** decimals);
        const finalBalance = parseFloat(balance.toString() + '.' + remainder.toString().padStart(decimals, '0'));

        return finalBalance;
      });

      return curBalance.toString();
    } catch (err) {
      console.error('Fetch contract balance error:', err);
      return null;
    }
  }

  /**
   * 編碼轉帳合約 ABI
   *
   * @param network 虛擬貨幣網路資訊
   * @param toAddress 接收方地址
   * @param amount 轉帳金額
   * @returns 編碼後的合約 ABI 字符串
   */
  transferContractABI(network: NetworksInterface, toAddress: string, amount: string) {
    const { contractABI, decimals } = network;

    // 使用 ethers.utils.parseUnits 來處理數量轉換
    // 它會回傳一個 BigNumber 物件，最後再轉成字串
    const amountToSend = ethers.parseUnits(amount, decimals).toString();

    // 使用 ethers.js 的 Interface 來編碼合約 ABI
    const iface = new Interface(JSON.parse(contractABI));
    // 編碼 transfer 函數的data
    const data = iface.encodeFunctionData('transfer', [toAddress, amountToSend]);

    return data;
  }

  /**
   * 發送交易 (例如：發送 ETH)
   *
   * @param transactionParameters
   * @returns 交易hash 或 null
   */
  private async _sendTransaction(transactionParameters: Web3TransactionConfig) {
    if (!this._isConnected.getValue() || !this.provider) {
      return null;
    }

    const showErrorMsg = () => {
      this.toastService.show({
        title: 'Deposit request failed',
        message: 'You rejected the transaction request',
        type: 'fail',
      });
    };

    try {
      // 使用 eth_sendTransaction 方法
      const txHash = await this.provider
        .request<string>({
          method: 'eth_sendTransaction',
          params: [transactionParameters],
        })
        .catch(err => {
          showErrorMsg();
          console.error('Coinbase Error sending transaction:', err);
          return null;
        });

      return txHash;
    } catch (err) {
      showErrorMsg();
      console.error('Coinbase Transaction failed:', err);
      return null;
    }
  }

  async sendMainTransaction(toAddress: string, amount: string, network: NetworksInterface): Promise<string | null> {
    const currentAccounts = this._accounts.getValue();
    if (!this._isConnected.getValue() || !this.provider || currentAccounts.length === 0) {
      return null;
    }

    // 在行動裝置上，因為關閉下拉選單的網路切換，所以要先打開 Coinbase Wallet App 並切換網路
    if (this.isMobileDevice) {
      this.openCoinbaseWalletApp();
      const hexChainId = '0x' + Number(network?.chainId).toString(16); // 將 chainId 轉換為十六進制
      await this.provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hexChainId }],
      });
    }

    const fromAddress = currentAccounts[0];

    // 將 Ether 金額轉換為 Wei (BigInt)
    const valueWei = ethers.parseEther(amount);

    const transactionParameters: Web3TransactionConfig = {
      from: fromAddress,
      to: toAddress,
      value: '0x' + valueWei.toString(16), // 轉換為十六進制字符串
    };

    return this._sendTransaction(transactionParameters);
  }

  async sendContractTransaction(toAddress: string, amount: string, network: NetworksInterface): Promise<string | null> {
    const currentAccounts = this._accounts.getValue();
    if (!this._isConnected.getValue() || !this.provider || currentAccounts.length === 0) {
      return null;
    }

    // 在行動裝置上，因為關閉下拉選單的網路切換，所以要先打開 Coinbase Wallet App 並切換網路
    if (this.isMobileDevice) {
      this.openCoinbaseWalletApp();
      const hexChainId = '0x' + Number(network?.chainId).toString(16); // 將 chainId 轉換為十六進制
      await this.provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hexChainId }],
      });
    }

    const fromAddress = currentAccounts[0];

    // 將 Ether 金額轉換為 Wei (BigInt)
    const valueWei = ethers.parseEther('0');

    const transactionParameters: Web3TransactionConfig = {
      from: fromAddress,
      to: network.contractAddress,
      value: '0x' + valueWei.toString(16), // 轉換為十六進制字符串
      data: this.transferContractABI(network, toAddress, amount),
    };

    return this._sendTransaction(transactionParameters);
  }

  /**
   * 切換到指定的網路
   *
   * @param chainId 要切換到的 Chain ID
   */
  async switchNetwork(chainId: number | string | null): Promise<void> {
    if (!this._isConnected.getValue() || !this.provider || chainId === null) {
      return;
    }

    const showErrorMsg = () => {
      this.toastService.show({
        title: 'Network not detected',
        message:
          'This network is not enabled in your wallet. Please enable it and try again or select a different network.',
        type: 'fail',
      });
    };

    // 檢查是否支援該網路，coinbase wallet 手機端因為不會連線確認，所以直接返回
    if (!this.supportedNetworks.includes(Number(chainId))) {
      showErrorMsg();
      console.error('Failed to switch network: Unsupported network');
      return Promise.reject('Unsupported network');
    }

    // coinbase wallet 在手機端無法透過連線來切換網路，避免返回錢包APP，直接略過返回
    if (this.isMobileDevice) {
      return;
    }

    try {
      const hexChainId = '0x' + Number(chainId).toString(16); // 將 chainId 轉換為十六進制

      await this.provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hexChainId }],
      });
    } catch (err) {
      // 顯示網路切換失敗
      showErrorMsg();
      console.error('Failed to switch network:', err);
      return Promise.reject(err);
    }
  }

  /**
   * 在 Coinbase Wallet 應用程式中打開 DApp
   *
   * @description 這個方法會將當前頁面 URL 編碼並構建一個通用連結，然後導向到這個連結
   */
  openInCoinbaseWalletBrowser() {
    // 對目標 URL 進行編碼
    const encodedTargetUrl = encodeURIComponent(window.location.href);

    // Coinbase Wallet 的通用連結
    const coinbaseWalletUrl = `https://go.cb-w.com/dapp?cb_url=${encodedTargetUrl}`;

    // 導向連結，觸發作業系統詢問是否在 Coinbase Wallet 中打開
    window.location.href = coinbaseWalletUrl;
  }

  openCoinbaseWalletApp() {
    // 導向連結，觸發作業系統詢問是否在 Coinbase Wallet 中打開
    const coinbaseWalletUrl = `https://go.cb-w.com/dapp`;
    window.location.href = coinbaseWalletUrl;
  }
}
